import { useMemo } from 'react';
import { Alert, Button, Empty, Popconfirm, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import type { PageMeta, Tariff, TariffType } from '@goelectric/shared-types';
import type { TariffFilters } from '../hooks/useTariffs';
import {
  formatDateTime,
  formatVigencia,
  representativePrice,
  TARIFF_TYPE_LABELS,
} from '../utils/format';
import { TariffOcpiBadge, TariffTypeBadge } from './TariffBadges';

const TYPE_OPTIONS: { value: TariffType | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Todos los tipos' },
  ...(['FLAT', 'TOU', 'IDLE_FEE'] as const).map((value) => ({
    value,
    label: TARIFF_TYPE_LABELS[value],
  })),
];

export interface TariffTableProps {
  tariffs: Tariff[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: TariffFilters;
  /** Muestra las acciones de escritura (editar/eliminar) y el CTA del empty-state. */
  canWrite: boolean;
  onFiltersChange: (filters: TariffFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (tariff: Tariff) => void;
  onEdit: (tariff: Tariff) => void;
  onDelete: (tariff: Tariff) => void;
}

/** Tabla CRUD de Tarifas (guía de estilo §8): filtro por tipo, paginación server-side, empty-state y acciones. */
export function TariffTable({
  tariffs,
  meta,
  loading,
  error,
  filters,
  canWrite,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
  onEdit,
  onDelete,
}: TariffTableProps) {
  const filtered = Boolean(filters.type);

  const columns = useMemo<ColumnsType<Tariff>>(
    () => [
      {
        title: 'Nombre',
        key: 'name',
        fixed: 'left',
        width: 260,
        render: (_, tariff) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{tariff.name}</Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {tariff.id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Tipo',
        dataIndex: 'type',
        key: 'type',
        width: 170,
        render: (type: TariffType) => <TariffTypeBadge type={type} />,
      },
      {
        title: 'Vigencia',
        key: 'vigencia',
        width: 250,
        render: (_, tariff) => formatVigencia(tariff.validFrom, tariff.validTo),
      },
      {
        title: 'Componentes / precio',
        key: 'price',
        width: 220,
        render: (_, tariff) => representativePrice(tariff),
      },
      {
        title: 'Marcada para OCPI',
        key: 'publishedToOcpi',
        width: 160,
        render: (_, tariff) => <TariffOcpiBadge published={tariff.publishedToOcpi ?? true} />,
      },
      {
        title: 'Actualizada',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 180,
        render: (value: string) => formatDateTime(value),
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: canWrite ? 240 : 90,
        align: 'right',
        render: (_, tariff) => (
          <Space size={4}>
            <Button
              size="small"
              aria-label={`Ver ${tariff.name}`}
              onClick={() => onSelect(tariff)}
            >
              Ver
            </Button>
            {canWrite && (
              <>
                <Button
                  size="small"
                  aria-label={`Editar ${tariff.name}`}
                  onClick={() => onEdit(tariff)}
                >
                  Editar
                </Button>
                <Popconfirm
                  title="Eliminar tarifa"
                  description="La tarifa quedará inactiva y se conservará el historial de las sesiones. Esta acción no se puede deshacer."
                  okText="Eliminar"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => onDelete(tariff)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Eliminar ${tariff.name}`}
                  >
                    Eliminar
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
      },
    ],
    [canWrite, onDelete, onEdit, onSelect],
  );

  const clearFilters = () => onFiltersChange({});

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las tarifas"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select<TariffType | 'ALL'>
          value={filters.type ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, type: value === 'ALL' ? undefined : value })
          }
          style={{ width: 220 }}
          aria-label="Filtrar por tipo de tarifa"
          options={TYPE_OPTIONS}
        />
      </Space>

      <Table<Tariff>
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={tariffs}
        scroll={{ x: 1460 }}
        pagination={{
          current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: false,
          showTotal: (total) => `${total} tarifas`,
          onChange: (page) => onOffsetChange((page - 1) * meta.limit),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filtered ? 'Sin tarifas para los filtros' : 'Aún no hay tarifas'}
            >
              {filtered ? (
                <Button onClick={clearFilters}>Limpiar filtros</Button>
              ) : (
                canWrite && (
                  <Button type="primary" onClick={onCreate}>
                    Crear la primera tarifa
                  </Button>
                )
              )}
            </Empty>
          ),
        }}
      />
    </div>
  );
}
